import React, { useEffect, useMemo, useState } from 'react';
import { DataTable, SectionFeedback, SummaryCard, TableControls, filterAndSortRows } from './CommercialCommon';

function getTodayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function getWeekStartIso(dateInput) {
  const base = dateInput ? new Date(`${dateInput}T12:00:00`) : new Date();
  const copy = new Date(base);
  const day = copy.getDay();
  const distanceToMonday = (day + 6) % 7;
  copy.setDate(copy.getDate() - distanceToMonday);
  return copy.toISOString().slice(0, 10);
}

function addDaysIso(dateInput, daysToAdd) {
  const base = new Date(`${dateInput}T12:00:00`);
  base.setDate(base.getDate() + daysToAdd);
  return base.toISOString().slice(0, 10);
}

function buildWeekDays(weekStart) {
  return Array.from({ length: 7 }, (_, index) => {
    const iso = addDaysIso(weekStart, index);
    const date = new Date(`${iso}T12:00:00`);
    return {
      iso,
      label: date.toLocaleDateString(undefined, { weekday: 'short' }),
      shortDate: date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }),
    };
  });
}

function rosterCellKey(technicianId, dateIso) {
  return `${technicianId}__${dateIso}`;
}

async function runPost({ api, endpoint, userEmail, payload, setFeedback, setSaving, successMessage, onSuccess }) {
  setFeedback({ error: '', success: '' });
  setSaving(true);
  try {
    await api.post(endpoint, { requesterEmail: userEmail, ...payload });
    setFeedback({ error: '', success: successMessage || 'Saved.' });
    onSuccess?.();
  } catch (err) {
    setFeedback({ error: err.response?.data?.message || 'Unable to save your changes.', success: '' });
  } finally {
    setSaving(false);
  }
}

async function runPatch({ api, endpoint, userEmail, payload, setFeedback, setSaving, successMessage, onSuccess }) {
  setFeedback({ error: '', success: '' });
  setSaving(true);
  try {
    await api.patch(endpoint, { requesterEmail: userEmail, ...payload });
    setFeedback({ error: '', success: successMessage || 'Changes saved.' });
    onSuccess?.();
  } catch (err) {
    setFeedback({ error: err.response?.data?.message || 'Unable to save your changes.', success: '' });
  } finally {
    setSaving(false);
  }
}

function printRoster({ tenantName, weekDays, rows }) {
  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1200,height=900');
  if (!printWindow) {
    window.print();
    return;
  }

  const headerCells = weekDays.map(day => `<th>${day.label}<br/><span>${day.shortDate}</span></th>`).join('');
  const bodyRows = rows.map(row => {
    const dayCells = weekDays.map(day => `<td>${row.printableByDate[day.iso] || '&mdash;'}</td>`).join('');
    return `<tr><td><strong>${row.displayName}</strong><br/><span>${row.teamName || ''}</span></td>${dayCells}</tr>`;
  }).join('');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${tenantName} Weekly Roster</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 24px; color: #102031; }
          h1 { margin: 0 0 8px; }
          p { margin: 0 0 18px; color: #4b6478; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { border: 1px solid #cdd8e3; padding: 10px; text-align: left; vertical-align: top; font-size: 12px; }
          th { background: #eef4fa; }
          th span { color: #5d7388; font-weight: 400; }
          td div { margin-bottom: 4px; }
          .pill { display: inline-block; padding: 2px 6px; border-radius: 999px; background: #fff2d6; color: #8b4b06; font-size: 11px; }
        </style>
      </head>
      <body>
        <h1>${tenantName} Weekly Roster</h1>
        <p>Week of ${weekDays[0]?.shortDate || ''} through ${weekDays[6]?.shortDate || ''}</p>
        <table>
          <thead>
            <tr>
              <th>Technician</th>
              ${headerCells}
            </tr>
          </thead>
          <tbody>
            ${bodyRows}
          </tbody>
        </table>
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

export default function CommercialWorkforceTab({ api, user, workspace, capabilities }) {
  const canManage = Boolean(capabilities?.canManageWorkspace);
  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const [loading, setLoading] = useState(false);
  const [weekStart, setWeekStart] = useState(getWeekStartIso(getTodayIsoDate()));
  const [plannerData, setPlannerData] = useState({
    rangeStart: '',
    rangeEnd: '',
    scheduleEntries: [],
    timeOffEntries: [],
    summary: {
      scheduledEntryCount: 0,
      timeOffEntryCount: 0,
      scheduledTechnicianCount: 0,
      awayTechnicianCount: 0,
    },
  });
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState('');
  const [timeOffSaving, setTimeOffSaving] = useState('');
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [selectedTimeOffId, setSelectedTimeOffId] = useState('');
  const [scheduleForm, setScheduleForm] = useState({
    technicianId: '',
    teamId: '',
    locationId: '',
    shiftId: '',
    workDate: getWeekStartIso(getTodayIsoDate()),
    startTimeLocal: '',
    endTimeLocal: '',
    assignmentType: 'scheduled',
    notes: '',
  });
  const [timeOffForm, setTimeOffForm] = useState({
    technicianId: '',
    startDate: getWeekStartIso(getTodayIsoDate()),
    endDate: addDaysIso(getWeekStartIso(getTodayIsoDate()), 1),
    requestType: 'vacation',
    status: 'approved',
    notes: '',
  });
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const [timeOffDraft, setTimeOffDraft] = useState(null);

  const weekDays = useMemo(() => buildWeekDays(weekStart), [weekStart]);

  const loadPlanner = async () => {
    if (!api || !user?.email) return;
    setLoading(true);
    setFeedback(current => ({ ...current, error: '' }));
    try {
      const res = await api.get('/commercial/workforce/planner', {
        params: {
          requesterEmail: user.email,
          startDate: weekStart,
          endDate: addDaysIso(weekStart, 6),
        },
      });
      setPlannerData(res.data);
    } catch (err) {
      setFeedback({
        error: err.response?.data?.message || 'Unable to load workforce planning data.',
        success: '',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanner();
  }, [api, user?.email, weekStart]);

  const scheduleEntries = plannerData.scheduleEntries || [];
  const timeOffEntries = plannerData.timeOffEntries || [];

  const selectedSchedule = useMemo(
    () => scheduleEntries.find(entry => entry.id === selectedScheduleId) || scheduleEntries[0] || null,
    [scheduleEntries, selectedScheduleId]
  );
  const selectedTimeOff = useMemo(
    () => timeOffEntries.find(entry => entry.id === selectedTimeOffId) || timeOffEntries[0] || null,
    [timeOffEntries, selectedTimeOffId]
  );

  useEffect(() => {
    if (selectedSchedule) {
      setSelectedScheduleId(selectedSchedule.id);
      setScheduleDraft({
        technicianId: selectedSchedule.technicianId || '',
        teamId: selectedSchedule.teamId || '',
        locationId: selectedSchedule.locationId || '',
        shiftId: selectedSchedule.shiftId || '',
        workDate: selectedSchedule.workDate || '',
        startTimeLocal: selectedSchedule.startTimeLocal || '',
        endTimeLocal: selectedSchedule.endTimeLocal || '',
        assignmentType: selectedSchedule.assignmentType || 'scheduled',
        notes: selectedSchedule.notes || '',
      });
    } else {
      setScheduleDraft(null);
    }
  }, [selectedSchedule]);

  useEffect(() => {
    if (selectedTimeOff) {
      setSelectedTimeOffId(selectedTimeOff.id);
      setTimeOffDraft({
        technicianId: selectedTimeOff.technicianId || '',
        startDate: selectedTimeOff.startDate || '',
        endDate: selectedTimeOff.endDate || '',
        requestType: selectedTimeOff.requestType || 'vacation',
        status: selectedTimeOff.status || 'approved',
        notes: selectedTimeOff.notes || '',
      });
    } else {
      setTimeOffDraft(null);
    }
  }, [selectedTimeOff]);

  const rosterRows = useMemo(() => {
    const filteredTechnicians = filterAndSortRows(workspace.technicians || [], {
      search,
      searchKeys: ['displayName', 'teamName', 'homeLocationName', 'shiftName'],
      sortKey: 'displayName',
      sortDirection: 'asc',
      filters: { teamName: teamFilter },
      filterAccessors: { teamName: row => row.teamName || '' },
    });

    const scheduleMap = new Map();
    scheduleEntries.forEach(entry => {
      const key = rosterCellKey(entry.technicianId, entry.workDate);
      const list = scheduleMap.get(key) || [];
      list.push(entry);
      scheduleMap.set(key, list);
    });

    const printableTimeOffLabel = entry => `<div><span class="pill">${entry.requestType.replaceAll('_', ' ')}</span> ${entry.status.replaceAll('_', ' ')}</div>`;
    const printableScheduleLabel = entry => `<div>${entry.startTimeLocal}-${entry.endTimeLocal} ${entry.assignmentType.replaceAll('_', ' ')}</div>`;

    return filteredTechnicians.map(technician => {
      const printableByDate = {};
      weekDays.forEach(day => {
        const scheduleForDay = scheduleMap.get(rosterCellKey(technician.id, day.iso)) || [];
        const timeOffForDay = timeOffEntries.filter(entry => (
          entry.technicianId === technician.id &&
          entry.startDate <= day.iso &&
          entry.endDate >= day.iso
        ));
        const printableSegments = [];
        if (timeOffForDay.length) printableSegments.push(...timeOffForDay.map(printableTimeOffLabel));
        if (scheduleForDay.length) printableSegments.push(...scheduleForDay.map(printableScheduleLabel));
        printableByDate[day.iso] = printableSegments.join('');
      });

      return {
        ...technician,
        printableByDate,
      };
    });
  }, [workspace.technicians, search, teamFilter, scheduleEntries, timeOffEntries, weekDays]);

  const scheduleRows = useMemo(() => filterAndSortRows(scheduleEntries, {
    search,
    searchKeys: ['technicianName', 'assignmentType', 'teamName', 'locationName', 'notes'],
    sortKey: 'workDate',
    sortDirection: 'asc',
    filters: { teamName: teamFilter },
    filterAccessors: { teamName: row => row.teamName || '' },
  }), [scheduleEntries, search, teamFilter]);

  const timeOffRows = useMemo(() => filterAndSortRows(timeOffEntries, {
    search,
    searchKeys: ['technicianName', 'requestType', 'status', 'notes'],
    sortKey: 'startDate',
    sortDirection: 'asc',
    filters: { teamName: teamFilter },
    filterAccessors: {
      teamName: row => (workspace.technicians.find(technician => technician.id === row.technicianId)?.teamName || ''),
    },
  }), [timeOffEntries, search, teamFilter, workspace.technicians]);

  const rosterColumns = useMemo(() => {
    const dayColumns = weekDays.map(day => ({
      key: day.iso,
      label: `${day.label} ${day.shortDate}`,
      render: row => {
        const scheduleForDay = scheduleEntries.filter(entry => entry.technicianId === row.id && entry.workDate === day.iso);
        const timeOffForDay = timeOffEntries.filter(entry => entry.technicianId === row.id && entry.startDate <= day.iso && entry.endDate >= day.iso);
        if (!scheduleForDay.length && !timeOffForDay.length) return '—';
        return (
          <div className="commercial-roster-cell">
            {timeOffForDay.map(entry => (
              <div key={entry.id} className="commercial-roster-pill commercial-roster-pill--timeoff">
                {entry.requestType.replaceAll('_', ' ')} ({entry.status.replaceAll('_', ' ')})
              </div>
            ))}
            {scheduleForDay.map(entry => (
              <div key={entry.id} className="commercial-roster-pill">
                {entry.startTimeLocal}-{entry.endTimeLocal} {entry.assignmentType.replaceAll('_', ' ')}
              </div>
            ))}
          </div>
        );
      },
    }));
    return [
      { key: 'displayName', label: 'Technician', render: row => <div><strong>{row.displayName}</strong><div className="commercial-form__hint">{row.teamName || row.shiftName || 'Unassigned team'}</div></div> },
      ...dayColumns,
    ];
  }, [weekDays, scheduleEntries, timeOffEntries]);

  const teamOptions = (workspace.teams || []).map(team => ({ value: team.name, label: team.name }));

  return (
    <>
      <section className="commercial-panel">
        <p className="commercial-panel__eyebrow">Workforce Planning</p>
        <h2 className="commercial-panel__title">Weekly Coverage and Time Off</h2>
        <p className="commercial-panel__copy">
          Plan technician coverage for the week, track vacation and other time off, and print a manager roster for posting.
        </p>
        <SectionFeedback error={feedback.error} success={feedback.success} />
      </section>

      <section className="commercial-panel">
        <div className="commercial-workforce-toolbar">
          <label className="commercial-form__field">
            <span>Week Starting</span>
            <input type="date" value={weekStart} onChange={event => setWeekStart(getWeekStartIso(event.target.value))} />
          </label>
          <button type="button" className="commercial-table-controls__ghost" onClick={loadPlanner} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh Week'}
          </button>
          <button
            type="button"
            className="commercial-table-controls__ghost"
            onClick={() => printRoster({ tenantName: workspace.tenant?.companyName || 'Company', weekDays, rows: rosterRows })}
          >
            Print Manager Roster
          </button>
        </div>
      </section>

      <section className="commercial-summary-grid">
        <SummaryCard label="Week Schedule Entries" value={plannerData.summary?.scheduledEntryCount || 0} />
        <SummaryCard label="People Scheduled" value={plannerData.summary?.scheduledTechnicianCount || 0} tone="accent" />
        <SummaryCard label="Time Off Entries" value={plannerData.summary?.timeOffEntryCount || 0} tone="warning" />
        <SummaryCard label="People Away" value={plannerData.summary?.awayTechnicianCount || 0} tone="success" />
      </section>

      {canManage ? (
        <section className="commercial-grid commercial-grid--equal">
          <section className="commercial-panel">
            <h3 className="commercial-section-title">Add Weekly Schedule Entry</h3>
            <div className="commercial-form commercial-form__grid">
              <label className="commercial-form__field">
                <span>Technician</span>
                <select value={scheduleForm.technicianId} onChange={event => setScheduleForm(current => ({ ...current, technicianId: event.target.value }))}>
                  <option value="">Select a technician</option>
                  {(workspace.technicians || []).map(technician => <option key={technician.id} value={technician.id}>{technician.displayName}</option>)}
                </select>
              </label>
              <label className="commercial-form__field">
                <span>Work Date</span>
                <input type="date" value={scheduleForm.workDate} onChange={event => setScheduleForm(current => ({ ...current, workDate: event.target.value }))} />
              </label>
              <label className="commercial-form__field">
                <span>Shift</span>
                <select
                  value={scheduleForm.shiftId}
                  onChange={event => {
                    const nextShiftId = event.target.value;
                    const nextShift = (workspace.shifts || []).find(shift => shift.id === nextShiftId);
                    setScheduleForm(current => ({
                      ...current,
                      shiftId: nextShiftId,
                      startTimeLocal: nextShift?.startTimeLocal || current.startTimeLocal,
                      endTimeLocal: nextShift?.endTimeLocal || current.endTimeLocal,
                    }));
                  }}
                >
                  <option value="">Select a shift</option>
                  {(workspace.shifts || []).map(shift => <option key={shift.id} value={shift.id}>{shift.name}</option>)}
                </select>
              </label>
              <label className="commercial-form__field">
                <span>Assignment Type</span>
                <select value={scheduleForm.assignmentType} onChange={event => setScheduleForm(current => ({ ...current, assignmentType: event.target.value }))}>
                  <option value="scheduled">Scheduled</option>
                  <option value="on_call">On Call</option>
                  <option value="training">Training</option>
                  <option value="coverage">Coverage</option>
                  <option value="overtime">Overtime</option>
                </select>
              </label>
              <label className="commercial-form__field">
                <span>Start Time</span>
                <input type="time" value={scheduleForm.startTimeLocal} onChange={event => setScheduleForm(current => ({ ...current, startTimeLocal: event.target.value }))} />
              </label>
              <label className="commercial-form__field">
                <span>End Time</span>
                <input type="time" value={scheduleForm.endTimeLocal} onChange={event => setScheduleForm(current => ({ ...current, endTimeLocal: event.target.value }))} />
              </label>
              <label className="commercial-form__field">
                <span>Team</span>
                <select value={scheduleForm.teamId} onChange={event => setScheduleForm(current => ({ ...current, teamId: event.target.value }))}>
                  <option value="">Use technician team</option>
                  {(workspace.teams || []).map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
              <label className="commercial-form__field">
                <span>Location</span>
                <select value={scheduleForm.locationId} onChange={event => setScheduleForm(current => ({ ...current, locationId: event.target.value }))}>
                  <option value="">Use technician location</option>
                  {(workspace.locations || []).map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
                </select>
              </label>
              <label className="commercial-form__field commercial-form__field--full">
                <span>Notes</span>
                <textarea rows={3} value={scheduleForm.notes} onChange={event => setScheduleForm(current => ({ ...current, notes: event.target.value }))} />
              </label>
              <div className="commercial-form__actions">
                <button
                  type="button"
                  disabled={scheduleSaving === 'create'}
                  onClick={() => {
                    setScheduleSaving('create');
                    runPost({
                      api,
                      endpoint: '/commercial/workforce/schedule-entries',
                      userEmail: user.email,
                      payload: scheduleForm,
                      setFeedback,
                      setSaving: value => setScheduleSaving(value ? 'create' : ''),
                      successMessage: 'Weekly schedule entry added.',
                      onSuccess: () => {
                        setScheduleForm({
                          technicianId: '',
                          teamId: '',
                          locationId: '',
                          shiftId: '',
                          workDate: weekStart,
                          startTimeLocal: '',
                          endTimeLocal: '',
                          assignmentType: 'scheduled',
                          notes: '',
                        });
                        loadPlanner();
                      },
                    });
                  }}
                >
                  {scheduleSaving === 'create' ? 'Saving...' : 'Add Schedule Entry'}
                </button>
              </div>
            </div>
          </section>

          <section className="commercial-panel">
            <h3 className="commercial-section-title">Add Time Off</h3>
            <div className="commercial-form commercial-form__grid">
              <label className="commercial-form__field">
                <span>Technician</span>
                <select value={timeOffForm.technicianId} onChange={event => setTimeOffForm(current => ({ ...current, technicianId: event.target.value }))}>
                  <option value="">Select a technician</option>
                  {(workspace.technicians || []).map(technician => <option key={technician.id} value={technician.id}>{technician.displayName}</option>)}
                </select>
              </label>
              <label className="commercial-form__field">
                <span>Start Date</span>
                <input type="date" value={timeOffForm.startDate} onChange={event => setTimeOffForm(current => ({ ...current, startDate: event.target.value }))} />
              </label>
              <label className="commercial-form__field">
                <span>End Date</span>
                <input type="date" value={timeOffForm.endDate} onChange={event => setTimeOffForm(current => ({ ...current, endDate: event.target.value }))} />
              </label>
              <label className="commercial-form__field">
                <span>Time Off Type</span>
                <select value={timeOffForm.requestType} onChange={event => setTimeOffForm(current => ({ ...current, requestType: event.target.value }))}>
                  <option value="vacation">Vacation</option>
                  <option value="pto">PTO</option>
                  <option value="sick">Sick</option>
                  <option value="training">Training</option>
                  <option value="personal">Personal</option>
                </select>
              </label>
              <label className="commercial-form__field">
                <span>Status</span>
                <select value={timeOffForm.status} onChange={event => setTimeOffForm(current => ({ ...current, status: event.target.value }))}>
                  <option value="approved">Approved</option>
                  <option value="planned">Planned</option>
                  <option value="pending">Pending</option>
                </select>
              </label>
              <label className="commercial-form__field commercial-form__field--full">
                <span>Notes</span>
                <textarea rows={3} value={timeOffForm.notes} onChange={event => setTimeOffForm(current => ({ ...current, notes: event.target.value }))} />
              </label>
              <div className="commercial-form__actions">
                <button
                  type="button"
                  disabled={timeOffSaving === 'create'}
                  onClick={() => {
                    setTimeOffSaving('create');
                    runPost({
                      api,
                      endpoint: '/commercial/workforce/time-off',
                      userEmail: user.email,
                      payload: timeOffForm,
                      setFeedback,
                      setSaving: value => setTimeOffSaving(value ? 'create' : ''),
                      successMessage: 'Time off added.',
                      onSuccess: () => {
                        setTimeOffForm({
                          technicianId: '',
                          startDate: weekStart,
                          endDate: addDaysIso(weekStart, 1),
                          requestType: 'vacation',
                          status: 'approved',
                          notes: '',
                        });
                        loadPlanner();
                      },
                    });
                  }}
                >
                  {timeOffSaving === 'create' ? 'Saving...' : 'Add Time Off'}
                </button>
              </div>
            </div>
          </section>
        </section>
      ) : null}

      <section className="commercial-panel">
        <h3 className="commercial-section-title">Weekly Manager Roster</h3>
        <TableControls
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search technicians"
          sortOptions={[]}
          sortKey=""
          onSortKeyChange={() => {}}
          sortDirection="asc"
          onSortDirectionChange={() => {}}
          filters={[{ key: 'teamName', label: 'Team', options: teamOptions, emptyLabel: 'All Teams' }]}
          filterValues={{ teamName: teamFilter }}
          onFilterChange={(key, value) => {
            if (key === 'teamName') setTeamFilter(value);
          }}
          onClear={() => {
            setSearch('');
            setTeamFilter('');
          }}
        />
        <DataTable columns={rosterColumns} rows={rosterRows} emptyMessage="No technicians available for this view." />
      </section>

      <section className="commercial-grid commercial-grid--equal">
        <section className="commercial-panel">
          <h3 className="commercial-section-title">Scheduled Entries This Week</h3>
          <DataTable
            columns={[
              { key: 'workDate', label: 'Date' },
              { key: 'technicianName', label: 'Technician' },
              { key: 'assignmentType', label: 'Type', render: row => row.assignmentType.replaceAll('_', ' ') },
              { key: 'startTimeLocal', label: 'Start' },
              { key: 'endTimeLocal', label: 'End' },
            ]}
            rows={scheduleRows}
            emptyMessage="No schedule entries for this week."
            onRowSelect={row => setSelectedScheduleId(row.id)}
            selectedRowId={selectedSchedule?.id}
          />
          {scheduleDraft ? (
            <div className="commercial-form" style={{ marginTop: 16 }}>
              <label className="commercial-form__field"><span>Technician</span><select value={scheduleDraft.technicianId} onChange={event => setScheduleDraft(current => ({ ...current, technicianId: event.target.value }))}><option value="">Select a technician</option>{(workspace.technicians || []).map(technician => <option key={technician.id} value={technician.id}>{technician.displayName}</option>)}</select></label>
              <label className="commercial-form__field"><span>Date</span><input type="date" value={scheduleDraft.workDate} onChange={event => setScheduleDraft(current => ({ ...current, workDate: event.target.value }))} /></label>
              <label className="commercial-form__field"><span>Type</span><select value={scheduleDraft.assignmentType} onChange={event => setScheduleDraft(current => ({ ...current, assignmentType: event.target.value }))}><option value="scheduled">Scheduled</option><option value="on_call">On Call</option><option value="training">Training</option><option value="coverage">Coverage</option><option value="overtime">Overtime</option></select></label>
              <label className="commercial-form__field"><span>Start</span><input type="time" value={scheduleDraft.startTimeLocal} onChange={event => setScheduleDraft(current => ({ ...current, startTimeLocal: event.target.value }))} /></label>
              <label className="commercial-form__field"><span>End</span><input type="time" value={scheduleDraft.endTimeLocal} onChange={event => setScheduleDraft(current => ({ ...current, endTimeLocal: event.target.value }))} /></label>
              <label className="commercial-form__field"><span>Shift</span><select value={scheduleDraft.shiftId || ''} onChange={event => setScheduleDraft(current => ({ ...current, shiftId: event.target.value }))}><option value="">No linked shift</option>{(workspace.shifts || []).map(shift => <option key={shift.id} value={shift.id}>{shift.name}</option>)}</select></label>
              <label className="commercial-form__field"><span>Team</span><select value={scheduleDraft.teamId || ''} onChange={event => setScheduleDraft(current => ({ ...current, teamId: event.target.value }))}><option value="">No team override</option>{(workspace.teams || []).map(team => <option key={team.id} value={team.id}>{team.name}</option>)}</select></label>
              <label className="commercial-form__field"><span>Location</span><select value={scheduleDraft.locationId || ''} onChange={event => setScheduleDraft(current => ({ ...current, locationId: event.target.value }))}><option value="">No location override</option>{(workspace.locations || []).map(location => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label>
              <label className="commercial-form__field commercial-form__field--full"><span>Notes</span><textarea rows={3} value={scheduleDraft.notes || ''} onChange={event => setScheduleDraft(current => ({ ...current, notes: event.target.value }))} /></label>
              {canManage ? (
                <div className="commercial-detail-actions">
                  <button type="button" disabled={scheduleSaving === 'update'} onClick={() => runPatch({ api, endpoint: `/commercial/workforce/schedule-entries/${selectedSchedule.id}`, userEmail: user.email, payload: scheduleDraft, setFeedback, setSaving: value => setScheduleSaving(value ? 'update' : ''), successMessage: 'Schedule entry updated.', onSuccess: loadPlanner })}>{scheduleSaving === 'update' ? 'Saving...' : 'Save Schedule Entry'}</button>
                  <button type="button" className="commercial-table-controls__ghost" disabled={scheduleSaving === 'archive'} onClick={() => runPost({ api, endpoint: `/commercial/workforce/schedule-entries/${selectedSchedule.id}/archive`, userEmail: user.email, payload: { archived: true }, setFeedback, setSaving: value => setScheduleSaving(value ? 'archive' : ''), successMessage: 'Schedule entry archived.', onSuccess: () => { setSelectedScheduleId(''); loadPlanner(); } })}>Archive</button>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="commercial-panel">
          <h3 className="commercial-section-title">Time Off This Week</h3>
          <DataTable
            columns={[
              { key: 'technicianName', label: 'Technician' },
              { key: 'requestType', label: 'Type', render: row => row.requestType.replaceAll('_', ' ') },
              { key: 'status', label: 'Status', render: row => row.status.replaceAll('_', ' ') },
              { key: 'startDate', label: 'Start' },
              { key: 'endDate', label: 'End' },
            ]}
            rows={timeOffRows}
            emptyMessage="No time off overlaps this week."
            onRowSelect={row => setSelectedTimeOffId(row.id)}
            selectedRowId={selectedTimeOff?.id}
          />
          {timeOffDraft ? (
            <div className="commercial-form" style={{ marginTop: 16 }}>
              <label className="commercial-form__field"><span>Technician</span><select value={timeOffDraft.technicianId} onChange={event => setTimeOffDraft(current => ({ ...current, technicianId: event.target.value }))}><option value="">Select a technician</option>{(workspace.technicians || []).map(technician => <option key={technician.id} value={technician.id}>{technician.displayName}</option>)}</select></label>
              <label className="commercial-form__field"><span>Start Date</span><input type="date" value={timeOffDraft.startDate} onChange={event => setTimeOffDraft(current => ({ ...current, startDate: event.target.value }))} /></label>
              <label className="commercial-form__field"><span>End Date</span><input type="date" value={timeOffDraft.endDate} onChange={event => setTimeOffDraft(current => ({ ...current, endDate: event.target.value }))} /></label>
              <label className="commercial-form__field"><span>Type</span><select value={timeOffDraft.requestType} onChange={event => setTimeOffDraft(current => ({ ...current, requestType: event.target.value }))}><option value="vacation">Vacation</option><option value="pto">PTO</option><option value="sick">Sick</option><option value="training">Training</option><option value="personal">Personal</option></select></label>
              <label className="commercial-form__field"><span>Status</span><select value={timeOffDraft.status} onChange={event => setTimeOffDraft(current => ({ ...current, status: event.target.value }))}><option value="approved">Approved</option><option value="planned">Planned</option><option value="pending">Pending</option></select></label>
              <label className="commercial-form__field commercial-form__field--full"><span>Notes</span><textarea rows={3} value={timeOffDraft.notes || ''} onChange={event => setTimeOffDraft(current => ({ ...current, notes: event.target.value }))} /></label>
              {canManage ? (
                <div className="commercial-detail-actions">
                  <button type="button" disabled={timeOffSaving === 'update'} onClick={() => runPatch({ api, endpoint: `/commercial/workforce/time-off/${selectedTimeOff.id}`, userEmail: user.email, payload: timeOffDraft, setFeedback, setSaving: value => setTimeOffSaving(value ? 'update' : ''), successMessage: 'Time-off entry updated.', onSuccess: loadPlanner })}>{timeOffSaving === 'update' ? 'Saving...' : 'Save Time Off'}</button>
                  <button type="button" className="commercial-table-controls__ghost" disabled={timeOffSaving === 'archive'} onClick={() => runPost({ api, endpoint: `/commercial/workforce/time-off/${selectedTimeOff.id}/archive`, userEmail: user.email, payload: { archived: true }, setFeedback, setSaving: value => setTimeOffSaving(value ? 'archive' : ''), successMessage: 'Time-off entry archived.', onSuccess: () => { setSelectedTimeOffId(''); loadPlanner(); } })}>Archive</button>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      </section>
    </>
  );
}
